import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '@/lib/v2/components';

import { geolocation } from '@/src/ContactsModule/utils/Geolocation';
import { ConditionState } from '@/src/modules/AutomationModuleNew/atoms/flows';
import {
  BELONG,
  CONDITIONS_SELECT,
  CUSTOM_FIELD,
  NOT_BELONG,
  TYPE_GROUPS,
  TYPE_TAGS,
} from '@/src/modules/AutomationModuleNew/constants/flows';

export const useGetSelectsBinaryConditionalOptions = () => {
  const { t } = useTranslation();

  const selectTypesOptions = useMemo<Option[]>(
    () => [
      {
        id: CUSTOM_FIELD,
        name: t('useBinaryConditional.customFields') as unknown as string,
        value: CUSTOM_FIELD,
      },
      {
        id: TYPE_GROUPS,
        name: t('useBinaryConditional.group') as unknown as string,
        value: TYPE_GROUPS,
      },
      {
        id: TYPE_TAGS,
        name: t('useBinaryConditional.tag') as unknown as string,
        value: TYPE_TAGS,
      },
    ],
    [t]
  );

  const selectBelongOptions = useMemo<Option[]>(
    () => [
      {
        id: BELONG,
        name: t('useBinaryConditional.belongs') as unknown as string,
        value: BELONG,
      },
      {
        id: NOT_BELONG,
        name: t('useBinaryConditional.notBelong') as unknown as string,
        value: NOT_BELONG,
      },
    ],
    [t]
  );

  const selectCFUniqueOptions = useMemo<Option[]>(
    () => [
      {
        id: CONDITIONS_SELECT.distinct,
        name: t('useBinaryConditional.distinct') as unknown as string,
        value: CONDITIONS_SELECT.distinct,
      },
      {
        id: CONDITIONS_SELECT.equals,
        name: t('useBinaryConditional.equals') as unknown as string,
        value: CONDITIONS_SELECT.equals,
      },
    ],
    [t]
  );

  const selectCFNumberOptions = useMemo<Option[]>(
    () => [
      {
        id: CONDITIONS_SELECT.equals,
        name: t('useBinaryConditional.equals') as unknown as string,
        value: CONDITIONS_SELECT.equals,
      },
      {
        id: CONDITIONS_SELECT.greaterThanOrEquals,
        name: t('useBinaryConditional.greaterThanOrEquals') as unknown as string,
        value: CONDITIONS_SELECT.greaterThanOrEquals,
      },
      {
        id: CONDITIONS_SELECT.lessThanOrEquals,
        name: t('useBinaryConditional.lessThanOrEquals') as unknown as string,
        value: CONDITIONS_SELECT.lessThanOrEquals,
      },
      {
        id: CONDITIONS_SELECT.otherThan,
        name: t('useBinaryConditional.otherThan') as unknown as string,
        value: CONDITIONS_SELECT.otherThan,
      },
    ],
    [t]
  );

  const selectCFMultipleOptions = useMemo<Option[]>(
    () => [
      {
        id: CONDITIONS_SELECT.contains,
        name: t('useBinaryConditional.contains') as unknown as string,
        value: CONDITIONS_SELECT.contains,
      },
      {
        id: CONDITIONS_SELECT.notContains,
        name: t('useBinaryConditional.notContains') as unknown as string,
        value: CONDITIONS_SELECT.notContains,
      },
      {
        id: CONDITIONS_SELECT.startsWith,
        name: t('useBinaryConditional.startsWith') as unknown as string,
        value: CONDITIONS_SELECT.startsWith,
      },
      {
        id: CONDITIONS_SELECT.endsWith,
        name: t('useBinaryConditional.endsWith') as unknown as string,
        value: CONDITIONS_SELECT.endsWith,
      },
      {
        id: CONDITIONS_SELECT.equals,
        name: t('useBinaryConditional.equals') as unknown as string,
        value: CONDITIONS_SELECT.equals,
      },
    ],
    [t]
  );

  const genderOptions = useMemo<Option[]>(
    () => [
      { id: 'female', name: t('SEGMENTS_CONDITIONS.female') as unknown as string, value: 'female' },
      { id: 'male', name: t('SEGMENTS_CONDITIONS.male') as unknown as string, value: 'male' },
    ],
    [t]
  );

  const countryOptions = useMemo<Option[]>(
    () =>
      Object.entries(geolocation).map(([id, name]) => ({
        id,
        name,
      })),
    []
  );

  const getSelectConditionsOptions = useCallback(
    (
      selectTypeValue: ConditionState['selectTypeValue'],
      selectCustomFieldsValue: ConditionState['selectCustomFieldsValue']
    ) => {
      if (selectTypeValue?.value === CUSTOM_FIELD) {
        if (!selectCustomFieldsValue) return selectCFMultipleOptions;
        if (selectCustomFieldsValue?.data.type === 'number') {
          return selectCFNumberOptions;
        }
        if (selectCustomFieldsValue?.data.metadata.optionType) {
          return selectCFUniqueOptions;
        }

        return selectCFMultipleOptions;
      }
      return selectBelongOptions;
    },
    [selectBelongOptions, selectCFMultipleOptions, selectCFNumberOptions, selectCFUniqueOptions]
  );

  return {
    selectBelongOptions,
    selectTypesOptions,
    selectCFUniqueOptions,
    selectCFNumberOptions,
    selectCFMultipleOptions,
    genderOptions,
    countryOptions,
    getSelectConditionsOptions,
  };
};
